// src/components/APanel/QuestionEditor/ExamNewTicketPopup.jsx
//
// Întrebări → Unelte → „Bilet nou din JSON” (examen), în sub-popup. Fosta pagină Bilete
// examen: fiecare întrebare din fișier devine o întrebare nouă (imaginea
// /EXAMEN_INSTRUIRE_AUTO/images/B{bilet}/image{ordine}.jpeg), apoi biletul ales primește
// exact id-urile create. Dedesubt, pe fiecare întrebare, ce s-a citit din fișier și ce
// se trimite la server.

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { createExamQuestion, updateExamTicket } from "../../../api/examAdminService";
import { toPositiveInt } from "../../../utils/examQuestionFormat";
import {
   buildExamImagePath,
   extractCreatedQuestionId,
   guessTicketNumberFromName,
   parseNewTicketImport,
} from "../../../utils/examNewTicketImport";
import { ButtonUI, CardUI, InputUI, ListUI, StatusUI } from "../../UI";
import { ImportWarnings, JsonFileButton, loadAllExamTickets, readJsonFile, safeList, uniqPositiveInts } from "./importTools";

export default function ExamNewTicketPopup({ onDone }) {
   const [tickets, setTickets] = useState([]);
   const [ticketsLoading, setTicketsLoading] = useState(true);
   const [ticketId, setTicketId] = useState("");
   const [fileName, setFileName] = useState("");
   const [items, setItems] = useState([]);
   const [creating, setCreating] = useState(false);
   const [notice, setNotice] = useState(null);
   const [createdIds, setCreatedIds] = useState([]);
   const [syncedIds, setSyncedIds] = useState([]);
   const [failures, setFailures] = useState([]);

   const loadTicketList = useCallback(async () => {
      setTicketsLoading(true);
      try {
         setTickets(await loadAllExamTickets());
      } catch (error) {
         setNotice({ tone: "error", text: `Nu am putut încărca biletele: ${error?.message || "eroare"}` });
      } finally {
         setTicketsLoading(false);
      }
   }, []);

   useEffect(() => {
      loadTicketList();
   }, [loadTicketList]);

   const ticket = tickets.find((row) => String(row.id) === String(ticketId)) || null;
   const ticketNoForImages = guessTicketNumberFromName(ticket?.name) || toPositiveInt(ticket?.id);

   // ce s-a citit din fișier și ce pleacă la server, pe fiecare întrebare
   const debugPreview = useMemo(
      () =>
         items.map((item) => {
            const order = item?.payload?.order;
            return {
               order,
               text: item?.source?.text || item?.source?.textRu || "",
               extracted: {
                  text: item?.source?.text,
                  textRu: item?.source?.textRu,
                  image: item?.source?.image,
                  answers: item?.source?.answers,
                  answersRu: item?.source?.answersRu,
                  correctAnswer: item?.source?.correctAnswer,
                  correctAnswerFallbackUsed: item?.source?.correctAnswerFallbackUsed,
                  categoryId: item?.source?.categoryId,
               },
               payload: {
                  text: item?.payload?.text,
                  image: buildExamImagePath(ticketNoForImages, order),
                  answers: item?.payload?.answers,
                  correctAnswer: item?.payload?.correctAnswer,
                  categoryId: item?.payload?.categoryId,
               },
            };
         }),
      [items, ticketNoForImages],
   );

   const resetResult = () => {
      setFailures([]);
      setCreatedIds([]);
      setSyncedIds([]);
   };

   const pickFile = async (file) => {
      resetResult();
      try {
         const normalized = parseNewTicketImport(await readJsonFile(file, { lenient: true }));
         setFileName(file.name);
         setItems(normalized.items);
         setNotice({
            tone: normalized.correctFallbackCount ? "warning" : "success",
            text: `Fișier: ${file.name} · ${normalized.items.length} întrebări · ${normalized.correctFallbackCount} fără răspuns corect explicit (primesc răspunsul 1).`,
         });
      } catch (error) {
         setFileName("");
         setItems([]);
         setNotice({ tone: "error", text: error?.message || "Fișier JSON invalid." });
      }
   };

   const createAndSync = async () => {
      if (!ticket?.id) return setNotice({ tone: "warning", text: "Alege întâi biletul." });
      if (!items.length) return setNotice({ tone: "warning", text: "Alege întâi fișierul cu întrebări." });

      setCreating(true);
      setNotice({ tone: "loading", text: "Se creează întrebările…" });
      resetResult();

      try {
         const created = [];
         const failed = [];

         for (const item of items) {
            const order = Number(item?.payload?.order ?? "?");
            const imagePath = buildExamImagePath(ticketNoForImages, order);

            try {
               if (!imagePath) throw new Error(`Nu pot face calea imaginii pentru întrebarea ${order}.`);

               const raw = await createExamQuestion({
                  text: item?.payload?.text,
                  image: imagePath,
                  answers: safeList(item?.payload?.answers).map((answer) => String(answer ?? "")),
                  correctAnswer: item?.payload?.correctAnswer,
                  categoryId: item?.payload?.categoryId,
               });

               const qid = extractCreatedQuestionId(raw);
               if (!qid) throw new Error(`Nu găsesc id-ul întrebării create (${order}).`);
               created.push(qid);
            } catch (error) {
               failed.push({ order, message: error?.message || "Eroare de la server." });
            }
         }

         const uniqCreated = uniqPositiveInts(created);
         setFailures(failed);
         if (!uniqCreated.length) throw new Error("Nu s-a creat nicio întrebare.");

         await updateExamTicket(ticket.id, {
            name: String(ticket?.name ?? "").trim() || `Ticket ${ticket.id}`,
            questionIds: uniqCreated,
         });

         setCreatedIds(uniqCreated);
         setSyncedIds(uniqCreated);
         setNotice(
            failed.length
               ? {
                    tone: "warning",
                    text: `Create ${uniqCreated.length} din ${items.length} și puse în bilet. Eșuate: ${failed.length}.`,
                 }
               : { tone: "success", text: `Gata: ${uniqCreated.length} întrebări create și puse în bilet.` },
         );

         await loadTicketList();
         onDone?.();
      } catch (error) {
         setNotice({ tone: "error", text: error?.message || "Nu am putut crea întrebările." });
      } finally {
         setCreating(false);
      }
   };

   const ticketOptions = [
      { value: "", label: ticketsLoading ? "Se încarcă biletele…" : "Alege biletul" },
      ...tickets.map((row) => ({ value: String(row.id), label: `#${row.id} · ${row.name || "(fără nume)"} · ${row.questionCount}` })),
   ];

   return (
      <CardUI
         level="popup"
         title="Bilet nou din JSON"
         description="Examen. Întrebările din fișier se creează din nou, iar biletul ales primește doar întrebările create acum."
      >
         <InputUI.Group columns={1}>
            <InputUI
               as="select"
               label="Biletul"
               value={ticketId}
               onChange={(e) => setTicketId(e.target.value)}
               options={ticketOptions}
               disabled={creating || ticketsLoading}
               hint={
                  ticket
                     ? `Imaginile: /EXAMEN_INSTRUIRE_AUTO/images/B${ticketNoForImages}/image{ordine}.jpeg`
                     : undefined
               }
            />
         </InputUI.Group>

         <ButtonUI.Row align="start">
            <JsonFileButton onFile={pickFile} disabled={creating} />
            <ButtonUI
               variant="primary"
               confirm="Da, creează"
               confirmTitle={ticket ? `${items.length} întrebări noi în #${ticket.id}` : undefined}
               loading={creating}
               disabled={creating || !ticket?.id || !items.length}
               onClick={createAndSync}
            >
               Creează și pune în bilet
            </ButtonUI>
         </ButtonUI.Row>

         {notice && <StatusUI tone={notice.tone}>{notice.text}</StatusUI>}

         {(createdIds.length > 0 || failures.length > 0) && (
            <ListUI columns={3}>
               <ListUI.Item layout="stat" title="Create acum" meta={createdIds.length} />
               <ListUI.Item layout="stat" title="Puse în bilet" meta={syncedIds.length} />
               <ListUI.Item layout="stat" title="Eșuate" meta={failures.length} tone={failures.length ? "bad" : undefined} />
            </ListUI>
         )}

         <ImportWarnings
            title={`Întrebări eșuate: ${failures.length}`}
            items={failures.map((failure) => `#${failure?.order ?? "?"}: ${failure?.message}`)}
            max={failures.length}
         />

         {fileName && (
            <ListUI title="Ce se trimite, pe întrebări" count={debugPreview.length}>
               {debugPreview.map((item, idx) => (
                  <ListUI.Item
                     key={`payload-preview-${item?.order ?? idx}`}
                     title={`Întrebarea ${item?.order}`}
                     detail={item.text}
                  >
                     <details className="questionsImport__details">
                        <summary>Extras din fișier și payload</summary>
                        <pre className="questionsImport__pre">{JSON.stringify(item.extracted, null, 2)}</pre>
                        <pre className="questionsImport__pre">{JSON.stringify(item.payload, null, 2)}</pre>
                     </details>
                  </ListUI.Item>
               ))}
            </ListUI>
         )}
      </CardUI>
   );
}
